import type { ComponentType, ImgHTMLAttributes, SVGProps } from 'react';
import {
  BadgeCheck,
  BriefcaseBusiness,
  BriefcaseMedical,
  Building2,
  Car,
  GraduationCap,
  Hammer,
  HeartPulse,
  Hospital,
  House,
  Landmark,
  MonitorCog,
  Paintbrush,
  Pill,
  Scale,
  Scissors,
  ShoppingBag,
  Sparkles,
  SprayCan,
  Store,
  Stethoscope,
  Truck,
  Users,
  Utensils,
  Wrench
} from 'lucide-react';
import dashboardIcon from '@renderer/assets/icons/dashboard-icon.svg';
import projectsIcon from '@renderer/assets/icons/projects.svg';
import tasksIcon from '@renderer/assets/icons/checkmark-square.svg';
import customersIcon from '@renderer/assets/icons/customer.svg';
import resourcesIcon from '@renderer/assets/icons/resources.svg';
import contractsIcon from '@renderer/assets/icons/contract.svg';
import invoicesIcon from '@renderer/assets/icons/invoice.svg';
import aviIcon from '@renderer/assets/icons/avi-ai-icon.svg';
import settingsIcon from '@renderer/assets/icons/settings.svg';
import helpIcon from '@renderer/assets/icons/help.svg';
import searchIcon from '@renderer/assets/icons/search-magnifier.svg';
import bellIcon from '@renderer/assets/icons/notification-bell.svg';
import plusIcon from '@renderer/assets/icons/plus-circle.svg';
import trashIcon from '@renderer/assets/icons/trash.svg';
import focusIcon from '@renderer/assets/icons/focus.svg';
import pencilIcon from '@renderer/assets/icons/edit-pencil.svg';
import restoreIcon from '@renderer/assets/icons/restore.svg';
import maximizeIcon from '@renderer/assets/icons/maximize-icon.svg';
import minimizeIcon from '@renderer/assets/icons/minimize-icon.svg';
import gridIcon from '@renderer/assets/icons/grid-view.svg';
import listIcon from '@renderer/assets/icons/list-view.svg';
import kanbanIcon from '@renderer/assets/icons/kanban-view.svg';
import syncIcon from '@renderer/assets/icons/sync.svg';
import builderIcon from '@renderer/assets/icons/builder-icon.svg';
import updateIcon from '@renderer/assets/icons/update-icon.svg';
import websiteIcon from '@renderer/assets/icons/website.svg';
import monitorIcon from '@renderer/assets/icons/monitor-icon.svg';
import chevronIcon from '@renderer/assets/icons/chevron-right.svg';
import arrowUpIcon from '@renderer/assets/icons/arrow-up.svg';
import arrowDownIcon from '@renderer/assets/icons/arrow-down.svg';
import duplicateIcon from '@renderer/assets/icons/duplicate.svg';
import eyeIcon from '@renderer/assets/icons/eye.svg';
import eyeOffIcon from '@renderer/assets/icons/eye-off.svg';
import workIcon from '@renderer/assets/icons/work.svg';
import euroIcon from '@renderer/assets/icons/euro.svg';
import documentIcon from '@renderer/assets/icons/document.svg';
import allIcon from '@renderer/assets/icons/all-icon.svg';
import filterIcon from '@renderer/assets/icons/filter.svg';
import imagesIcon from '@renderer/assets/icons/images.svg';
import iconIcon from '@renderer/assets/icons/icon.svg';
import timerIcon from '@renderer/assets/icons/timer-icon.svg';
import calendarIcon from '@renderer/assets/icons/calendar-date.svg';
import wordpressIcon from '@renderer/assets/icons/wordpress.svg';
import phpIcon from '@renderer/assets/icons/php.svg';
import userIcon from '@renderer/assets/icons/user.svg';
import addNoteIcon from '@renderer/assets/icons/add-note.svg';
import starIcon from '@renderer/assets/icons/star.svg';
import phoneIcon from '@renderer/assets/icons/phone.svg';
import locationIcon from '@renderer/assets/icons/location.svg';
import whatsappIcon from '@renderer/assets/icons/whatsapp.svg';
import checkIcon from '@renderer/assets/icons/check.svg';
import timesIcon from '@renderer/assets/icons/times.svg';
import facebookIcon from '@renderer/assets/icons/facebook.svg';
import instagramIcon from '@renderer/assets/icons/instagram.svg';
import googleBusinessIcon from '@renderer/assets/icons/google-my-business.svg';
import tiktokIcon from '@renderer/assets/icons/tiktok.svg';
import youtubeIcon from '@renderer/assets/icons/youtube.svg';
import alttvisionIcon from '@renderer/assets/icons/alttvision-icon.svg';

const iconSources = {
  dashboard: dashboardIcon,
  projects: projectsIcon,
  leads: phoneIcon,
  tasks: tasksIcon,
  customers: customersIcon,
  resources: resourcesIcon,
  contracts: contractsIcon,
  invoices: invoicesIcon,
  payments: euroIcon,
  avi: aviIcon,
  settings: settingsIcon,
  help: helpIcon,
  search: searchIcon,
  bell: bellIcon,
  plus: plusIcon,
  trash: trashIcon,
  focus: focusIcon,
  pencil: pencilIcon,
  restore: restoreIcon,
  maximize: maximizeIcon,
  minimize: minimizeIcon,
  grid: gridIcon,
  list: listIcon,
  kanban: kanbanIcon,
  sync: syncIcon,
  builder: builderIcon,
  update: updateIcon,
  website: websiteIcon,
  monitor: monitorIcon,
  chevron: chevronIcon,
  arrowUp: arrowUpIcon,
  arrowDown: arrowDownIcon,
  duplicate: duplicateIcon,
  eye: eyeIcon,
  eyeOff: eyeOffIcon,
  work: workIcon,
  euro: euroIcon,
  document: documentIcon,
  all: allIcon,
  filter: filterIcon,
  images: imagesIcon,
  icon: iconIcon,
  timer: timerIcon,
  calendar: calendarIcon,
  wordpress: wordpressIcon,
  php: phpIcon,
  user: userIcon,
  addNote: addNoteIcon,
  star: starIcon,
  phone: phoneIcon,
  location: locationIcon,
  whatsapp: whatsappIcon,
  check: checkIcon,
  times: timesIcon,
  facebook: facebookIcon,
  instagram: instagramIcon,
  googleBusiness: googleBusinessIcon,
  tiktok: tiktokIcon,
  youtube: youtubeIcon,
  alttvision: alttvisionIcon
} as const;

export const businessIconSources = {
  badgeCheck: BadgeCheck,
  briefcaseBusiness: BriefcaseBusiness,
  briefcaseMedical: BriefcaseMedical,
  building2: Building2,
  car: Car,
  graduationCap: GraduationCap,
  hammer: Hammer,
  heartPulse: HeartPulse,
  hospital: Hospital,
  house: House,
  landmark: Landmark,
  monitorCog: MonitorCog,
  paintbrush: Paintbrush,
  pill: Pill,
  scale: Scale,
  scissors: Scissors,
  shoppingBag: ShoppingBag,
  sparkles: Sparkles,
  sprayCan: SprayCan,
  store: Store,
  stethoscope: Stethoscope,
  truck: Truck,
  users: Users,
  utensils: Utensils,
  wrench: Wrench
} as const satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

export type BusinessIconName = keyof typeof businessIconSources;
export type IconName = keyof typeof iconSources | BusinessIconName;

export const businessIconOptions: Array<{ value: BusinessIconName; labelKey: string }> = [
  { value: 'briefcaseBusiness', labelKey: 'icons.business.briefcaseBusiness' },
  { value: 'building2', labelKey: 'icons.business.building2' },
  { value: 'scale', labelKey: 'icons.business.scale' },
  { value: 'sprayCan', labelKey: 'icons.business.sprayCan' },
  { value: 'stethoscope', labelKey: 'icons.business.stethoscope' },
  { value: 'briefcaseMedical', labelKey: 'icons.business.briefcaseMedical' },
  { value: 'shoppingBag', labelKey: 'icons.business.shoppingBag' },
  { value: 'store', labelKey: 'icons.business.store' },
  { value: 'hammer', labelKey: 'icons.business.hammer' },
  { value: 'utensils', labelKey: 'icons.business.utensils' },
  { value: 'graduationCap', labelKey: 'icons.business.graduationCap' },
  { value: 'heartPulse', labelKey: 'icons.business.heartPulse' },
  { value: 'hospital', labelKey: 'icons.business.hospital' },
  { value: 'landmark', labelKey: 'icons.business.landmark' },
  { value: 'pill', labelKey: 'icons.business.pill' },
  { value: 'sparkles', labelKey: 'icons.business.sparkles' },
  { value: 'users', labelKey: 'icons.business.users' },
  { value: 'wrench', labelKey: 'icons.business.wrench' },
  { value: 'scissors', labelKey: 'icons.business.scissors' },
  { value: 'paintbrush', labelKey: 'icons.business.paintbrush' },
  { value: 'house', labelKey: 'icons.business.house' },
  { value: 'truck', labelKey: 'icons.business.truck' },
  { value: 'car', labelKey: 'icons.business.car' },
  { value: 'monitorCog', labelKey: 'icons.business.monitorCog' },
  { value: 'badgeCheck', labelKey: 'icons.business.badgeCheck' }
];

interface IconProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  name: IconName;
  label?: string;
}

function isAssetIconName(name: IconName): name is keyof typeof iconSources {
  return name in iconSources;
}

export function isBusinessIconName(name: string): name is BusinessIconName {
  return name in businessIconSources;
}

export function normalizeBusinessIconName(name: string | null | undefined): BusinessIconName {
  return name && isBusinessIconName(name) ? name : 'briefcaseBusiness';
}

export function Icon({ name, className, label, ...props }: IconProps): JSX.Element {
  const resolvedClassName = className ? `av-icon ${className}` : 'av-icon';

  if (!isAssetIconName(name)) {
    const LucideIcon = businessIconSources[normalizeBusinessIconName(name)];

    return (
      <LucideIcon
        aria-hidden={label ? undefined : true}
        aria-label={label}
        className={resolvedClassName}
        focusable="false"
      />
    );
  }

  return (
    <img
      {...props}
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      className={resolvedClassName}
      src={iconSources[name]}
    />
  );
}
